import { XGridDefaultColumn } from '@AMIEWEB/Common/XGrid/cells/XGridDefaults';
import { GridPreProcessEditCellProps } from '@mui/x-data-grid';
import { TimeOffToolTip } from './TimeOffTooltip';
import { xGridDateSort, xGridNumberSort } from '@AMIEWEB/Common';
import { StartDateEditCell } from './EditTimeOff/StartDateTimeOff';
import { EndDateEditCell } from './EditTimeOff/EndDateTimeOff';
import { TimeOffHoursCell } from './EditTimeOff/TimeOffHours';
import { DailyCostTimeOffCell } from './EditTimeOff/DailyCostTimeOff';
import { TimeOffPayerCell } from './EditTimeOff/PayerTimeOff';
import { FacilityApprovedCell } from './EditTimeOff/FacilityApproved';
import moment from 'moment';
import { isBefore, isSameDay, isValid, isWithinInterval } from 'date-fns';
import { ErrorTootlTipHour } from './EditTimeOff/ErrorTootlTipHour';
import { EndDateToolTip } from './EditTimeOff/EndDateToolTip';
import { StartDateToolTip } from './EditTimeOff/StartDateToolTip';
import { isNumber } from '../../ModificationsUtils';
import { missingField } from 'app/constants';

export const defaultTimeOffColumn = {
  ...XGridDefaultColumn,
  flex: 0,
};

export const isInValidHours = params => {
  return !isNumber(params.row?.hours) || params.row.hours === missingField;
};

export const isInValidStartDate = params => {
  return (
    !params.row?.startDate ||
    params.row.startDate === missingField ||
    moment(params?.row.startDate) > moment(params?.row.endDate)
  );
};

export const isInValidEndDate = params => {
  return (
    !params.row?.endDate ||
    params.row.endDate === missingField ||
    moment(params?.row.startDate) > moment(params?.row.endDate)
  );
};
export const checkOverlapForArray = (timeOffEditRequests, params) => {
  const existingRangeArray = timeOffEditRequests
    ?.filter(
      item =>
        item?.startDate &&
        item?.endDate &&
        params?.row?.id !== item?.id &&
        !isBefore(new Date(item?.endDate), new Date(item?.startDate)) &&
        !isSameDay(new Date(item?.endDate), new Date(item?.startDate)),
    )
    ?.map(item => ({
      startDate: new Date(item?.startDate),
      endDate: new Date(item?.endDate),
    }));
  const newRange = { startDate: new Date(params?.row?.startDate), endDate: new Date(params?.row?.endDate) };
  if (existingRangeArray?.length > 0) {
    for (let eachRange of existingRangeArray) {
      if (isValid(newRange?.startDate) && !isValid(newRange?.endDate)) {
        const overlap = isWithinInterval(newRange?.startDate, {
          start: eachRange?.startDate,
          end: eachRange?.endDate,
        });
        if (overlap) {
          return true;
        }
      } else if (!isValid(newRange?.startDate) && isValid(newRange?.endDate)) {
        const overlap = isWithinInterval(newRange?.endDate, {
          start: eachRange?.startDate,
          end: eachRange?.endDate,
        });
        if (overlap) {
          return true;
        }
      } else if (isValid(newRange?.startDate) && isValid(newRange?.endDate)) {
        const overlap =
          isWithinInterval(eachRange?.endDate, {
            start: newRange?.startDate,
            end: newRange?.endDate,
          }) ||
          isWithinInterval(eachRange?.startDate, {
            start: newRange?.startDate,
            end: newRange?.endDate,
          }) ||
          isWithinInterval(newRange?.endDate, {
            start: eachRange?.startDate,
            end: eachRange?.endDate,
          }) ||
          isWithinInterval(newRange?.startDate, {
            start: eachRange?.startDate,
            end: eachRange?.endDate,
          });
        if (overlap) {
          return true;
        }
      }
    }
    return false;
  }
  return false;
};
export const getTimeOffGridColumns = (t, isExpanded, isModEdit, classes, timeOffEditRequests, setErrors) => [
  {
    ...defaultTimeOffColumn,
    field: 'startDate',
    headerName: isModEdit ? `${t('placement.placementMods.startDate')}*` : t('placement.placementMods.startDate'),
    width: isExpanded ? 158 : 100,
    hide: false,
    type: 'Date',
    editable: isModEdit,
    hideable: !isModEdit,
    renderEditCell: StartDateEditCell,
    preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
      if (isInValidStartDate(params)) {
        return { ...params.props, error: true, errorField: 'startDate' };
      } else if (checkOverlapForArray(timeOffEditRequests, params)) {
        return { ...params.props, error: true, errorField: 'startDate' };
      } else {
        setErrors(prevErrors => ({
          ...prevErrors,
          id: params.id,
        }));
        return { ...params.props, error: false, errorField: null };
      }
    },
    cellClassName: params => {
      if (isInValidStartDate(params)) {
        return classes.isErrorCell;
      } else if (checkOverlapForArray(timeOffEditRequests, params)) {
        return classes.isErrorCell;
      } else {
        return null;
      }
    },
    renderCell: StartDateToolTip,
    sortComparator: xGridDateSort,
  },
  {
    ...defaultTimeOffColumn,
    field: 'endDate',
    headerName: isModEdit ? `${t('placement.placementMods.endDate')}*` : t('placement.placementMods.endDate'),
    width: isExpanded ? 152 : 100,
    hide: false,
    hideable: !isModEdit,
    type: 'Date',
    editable: isModEdit,
    preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
      if (isInValidEndDate(params)) {
        return { ...params.props, error: true, errorField: 'endDate' };
      } else if (checkOverlapForArray(timeOffEditRequests, params)) {
        return { ...params.props, error: true, errorField: 'endDate' };
      } else {
        return { ...params.props, error: false, errorField: null };
      }
    },
    cellClassName: params => {
      if (isInValidEndDate(params)) {
        return classes.isErrorCell;
      } else if (checkOverlapForArray(timeOffEditRequests, params)) {
        return classes.isErrorCell;
      } else {
        return null;
      }
    },
    renderEditCell: EndDateEditCell,
    renderCell: EndDateToolTip,
    sortComparator: xGridDateSort,
  },
  {
    ...defaultTimeOffColumn,
    headerName: t('placement.placementMods.days'),
    field: 'days',
    width: isExpanded ? 152 : 80,
    hide: false,
    align: 'right',
    type: 'number',
    sortComparator: xGridNumberSort,
    preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
      return { ...params.props };
    },
  },
  {
    ...defaultTimeOffColumn,
    headerName: isModEdit ? `${t('placement.placementMods.hours')}*` : t('placement.placementMods.hours'),
    field: 'hours',
    align: 'right',
    hideable: !isModEdit,
    width: isExpanded ? 157 : 80,
    hide: false,
    type: 'number',
    editable: isModEdit,
    renderCell: ErrorTootlTipHour,
    preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
      if (isInValidHours(params)) {
        return { ...params.props.row, error: true, errorField: 'hours' };
      } else {
        return { ...params.props, error: false, errorField: null };
      }
    },
    renderEditCell: TimeOffHoursCell,
    cellClassName: params => {
      if (isInValidHours(params)) {
        return classes.isErrorCell;
      } else {
        return null;
      }
    },
    sortComparator: xGridNumberSort,
  },
  {
    ...defaultTimeOffColumn,
    headerName: t('placement.placementMods.cost'),
    field: 'cost',
    width: isExpanded ? 149 : 87,
    hide: false,
    editable: isModEdit,
    renderEditCell: DailyCostTimeOffCell,
    renderCell: params => (params.row.cost ? `$${params.row.cost}` : '--'),
    align: 'right',
  },
  {
    ...defaultTimeOffColumn,
    headerName: t('placement.placementMods.timeOffPayerDescription'),
    field: 'timeOffPayerDescription',
    width: isExpanded ? 238 : 149,
    editable: isModEdit,
    renderEditCell: TimeOffPayerCell,
    renderCell: params => (params.row.timeOffPayerDescription ? params.row.timeOffPayerDescription : '--'),
    hide: false,
  },
  {
    ...defaultTimeOffColumn,
    headerName: t('placement.placementMods.facilityApprovedBy'),
    field: 'facilityApprovedBy',
    width: isExpanded ? 488 : 182,
    hide: false,
    editable: isModEdit,
    renderEditCell: FacilityApprovedCell,
    renderCell: TimeOffToolTip,
  },
];
